// @flow

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ExpandMoreIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import GrossRentChangeDrawerActionsMenu from './GrossRentChangeDrawerActionsMenu';
import messages from './messages';
import type { GrossRentChangeDrawerSummaryProps } from './types';

const GrossRentChangeDrawerSummary = ({
  effectiveDate,
  anticipatedVoucherDate,
  expanded,
  isLocked = false,
  hasCompletedCertifications = false,
  onEdit,
}: GrossRentChangeDrawerSummaryProps) => {
  const effectiveDateValue = formatDateDisplayUTC(effectiveDate, 'MM/DD/YYYY');
  const voucherDateValue = anticipatedVoucherDate
    ? formatDateDisplayUTC(anticipatedVoucherDate, 'MMMM YYYY')
    : '---';

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ width: '100%' }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Chip
          label={
            <Typography
              variant="labelMedium"
              component="span"
              color="text.primary"
            >
              <FormattedMessage
                {...messages.effectiveDateLabel}
                values={{ date: effectiveDateValue }}
              />
            </Typography>
          }
          sx={{
            height: 'auto',
            borderRadius: 1,
            backgroundColor: 'transparent',
            '& .MuiChip-label': { px: 1, py: 0.5 },
          }}
        />
        <Chip
          label={
            <Typography
              variant="labelMedium"
              component="span"
              sx={{ color: 'primary.dark' }}
            >
              <FormattedMessage
                {...messages.voucherDateLabel}
                values={{ date: voucherDateValue }}
              />
            </Typography>
          }
          sx={{
            height: 'auto',
            borderRadius: 1,
            backgroundColor: 'primary.lighter',
            '& .MuiChip-label': { px: 1, py: 0.5 },
          }}
        />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ height: 40, flexShrink: 0 }}
      >
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            color: 'text.secondary',
            flexShrink: 0,
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
        <Box
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <GrossRentChangeDrawerActionsMenu
            effectiveDate={effectiveDate}
            isLocked={isLocked}
            hasCompletedCertifications={hasCompletedCertifications}
            onEdit={onEdit}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default React.memo(GrossRentChangeDrawerSummary);
